import { useMemo, useState } from 'react';
import { Inline, Stack, Text, textVariants, vars } from '@etg/wings';
import { useDirection, useSiteContext, useTranslation } from '@eti/providers';
import { css, etiColors, resetButtonStyle } from '@eti/styles';
import { CaretRight } from '@phosphor-icons/react';
import { Overlay } from '@eti/components';
import type { CurrencyAmount } from '@eti/schema-types';
import { ProviderBookingResult } from '@eti/schema-types';
import { screenReadersOnlyStyles } from '../../../../utils/styleUtils';
import { formatAmountInChargingCurrency, priceWithFullNameCurrency } from '../../utils';
import type { RefundCasesType, RefundPayoutsType } from './models';
import RefundedAmountBreakdown from './RefundedAmountBreakdown';

const spaceBetweenStyles = css`
  justify-content: space-between;
`;

const buttonStyles = css`
  ${resetButtonStyle}; /* stylelint-disable-next-line */
  cursor: pointer;
  padding-inline-start: 6px;
  position: relative;
  text-align: start;

  div::after {
    border: 2px solid ${vars.colors.focusRing};
    border-radius: 6px;
    content: '';
    height: calc(100% + 12px);
    left: -6px;
    opacity: 0;
    position: absolute;
    top: -6px;
    transition: opacity 0.2s ease-in-out;
    width: calc(100% + 12px);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible div::after {
    opacity: 1;
  }
`;

interface RefundedAmountProps {
  cases: RefundCasesType;
  totalRefundedAmount: CurrencyAmount;
}

const RefundedAmount = ({ cases, totalRefundedAmount }: RefundedAmountProps) => {
  const { t } = useTranslation();
  const { isRTL } = useDirection();
  const { language } = useSiteContext();
  const [isOpen, setIsOpen] = useState(false);

  const isAnyCaseInProgress = cases.some((cs) =>
    cs.providerBookings.some((pr) => {
      if (pr.__typename === 'RefundCaseProviderBooking') {
        return (
          (pr.providerBookingResult !== ProviderBookingResult.Success && pr.steps.length === 0) ||
          (pr.steps.length && !pr.steps[pr.steps.length - 1].completed)
        );
      }
      return false;
    }),
  );

  const refundPayouts: RefundPayoutsType = useMemo(
    () => cases.flatMap((c) => c.refundPayouts),
    [cases],
  );

  const priceInChargingCurrency = formatAmountInChargingCurrency(
    language.code,
    totalRefundedAmount.value,
    totalRefundedAmount.currency,
  );

  return (
    <Inline className={spaceBetweenStyles} noWrap>
      <Stack spacing={4}>
        <Text>
          {t(
            isAnyCaseInProgress
              ? 'MyBookings.RefundStatus.PartialRefund'
              : 'MyBookings.RefundStatus.TotalRefund',
          )}
        </Text>
        <Text aria-hidden variant={textVariants._18_EMPHASIZED}>
          <bdi>{priceInChargingCurrency}</bdi>
        </Text>
        <div className={screenReadersOnlyStyles}>
          {priceWithFullNameCurrency(
            priceInChargingCurrency,
            language.code,
            totalRefundedAmount.currency,
          )}
        </div>
      </Stack>
      <button
        aria-controls="price-breakdown-drawer"
        aria-expanded={isOpen ? 'true' : 'false'}
        aria-haspopup="dialog"
        className={buttonStyles}
        data-testid="price-breakdown-button"
        onClick={() => setIsOpen(true)}
        type="button"
      >
        <Inline alignY="center" noWrap>
          <Text>{t('MyBookings.RefundStatus.ViewPriceBreakdown')}</Text>
          <CaretRight
            aria-hidden="true"
            color={etiColors.grayScale[9]}
            mirrored={isRTL}
            size={24}
          />
        </Inline>
      </button>
      <Overlay.Drawer
        dataTestId="price-breakdown-drawer"
        handleClose={() => setIsOpen(false)}
        id="price-breakdown-drawer"
        isOpen={isOpen}
      >
        <Overlay.Header
          onClose={() => setIsOpen(false)}
          title={t('MyBookings.RefundStatus.PayoutDrawer.Title')}
        />
        <Overlay.Content data-testid="price-breakdown-drawer-content">
          <RefundedAmountBreakdown refundPayouts={refundPayouts} />
        </Overlay.Content>
      </Overlay.Drawer>
    </Inline>
  );
};

export default RefundedAmount;
